import { TranslationContext } from 'contexts/translation-context';

import { useContext } from 'react';
import styled from 'styled-components';
import { BlueLink } from 'components/common/link';
import { SuccessPageMobile } from './success-page-mobile';

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Card = styled.div`
  width: 100%;
  max-width: 760px;
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.siteCardBorder};
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  padding: 34px 56px 48px;
  box-sizing: border-box;
`;
const FirstText = styled.div`
  font-family: ${props => props.theme.fonts.FunnelDisplay};
  color: ${props => props.theme.colors.black};
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  margin-bottom: 18px;
`;
const SecondText = styled.div`
  font-family: ${props => props.theme.fonts.interSans};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  white-space: break-spaces;
`;
const BoldText = styled.div`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  color: ${props => props.theme.colors.black};
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
  white-space: break-spaces;
`;
const ThirdText = styled.div`
  font-family: ${props => props.theme.fonts.interSans};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
  white-space: break-spaces;
  max-width: 560px;
  margin: 0 auto 28px;
`;
const StyledLink = styled(BlueLink)`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  color: ${props => props.theme.colors.siteIndigo};
  font-size: 14px;
  text-decoration: auto;
`;
const WhiteBox = styled.div`
  border: 1px solid ${props => props.theme.colors.siteCardBorder};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.siteBlack};
  width: 100%;
  max-width: 560px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding: 28px 28px 24px;
  box-sizing: border-box;
  margin: 0 auto 26px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  ${SecondText} {
    text-align: left;
  }
`;
const MainListItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding-left: 22px;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.theme.colors.siteIndigo};
  }
`;
const ListItem = styled.div`
  position: relative;
  display: block;
  width: 100%;
  padding-left: 18px;
  margin-left: 22px;
  box-sizing: border-box;
`;

const NestedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: -3px;
`;
const InlineText = styled.span`
  font-family: ${props => props.theme.fonts.interSans};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 14px;
  line-height: 1.45;
`;
const InlineBoldText = styled(InlineText)`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  color: ${props => props.theme.colors.black};
`;
const EmailLink = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 560px;
  padding: 0 0 22px;
  margin: 0 auto 18px;
  border-bottom: 1px solid ${props => props.theme.colors.lightGrey};
`;
const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: ${props => props.theme.colors.siteBlack};
  max-width: 620px;
  margin: 0 auto;
`;

export const SuccessPage = () => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  // Use window.innerWidth to determine which component to render
  if (typeof window !== 'undefined' && window.innerWidth <= 999) {
    return <SuccessPageMobile />;
  }
  const handleContactUsClick = () => {
    window.open('https://www.rapnet.com/contact/support/', '_blank');
  };
  return (
    <Container>
      <Card>
        <FirstText>{TRANSLATION_TEXT.thank_you_for_subscribing}</FirstText>
        <ThirdText>{TRANSLATION_TEXT.process_your_rapnet_membership}</ThirdText>
        <WhiteBox>
          <MainListItem>
            <BoldText>{TRANSLATION_TEXT.active_business_license_or_certificate}</BoldText>
            <SecondText>{TRANSLATION_TEXT.showing_your_involvement}</SecondText>
          </MainListItem>
          <MainListItem>
            <BoldText>{TRANSLATION_TEXT.government_issued_photo_id}</BoldText>
            <SecondText>{TRANSLATION_TEXT.necessary_for_the_company_owner}</SecondText>
          </MainListItem>
          <MainListItem>
            <BoldText>{TRANSLATION_TEXT.trade_reputation_and_industry_affiliation}</BoldText>
          </MainListItem>
          <NestedList>
            <ListItem>
              <InlineBoldText>{TRANSLATION_TEXT.two_trade_references}</InlineBoldText>{' '}
              <InlineText>
                {TRANSLATION_TEXT.from_reputable_diamond_or_jewelry_companies}
              </InlineText>{' '}
              <InlineText>{TRANSLATION_TEXT.last_six_months}</InlineText>
            </ListItem>
            <ListItem>
              <InlineBoldText>{TRANSLATION_TEXT.two_trade_invoices}</InlineBoldText>{' '}
              <InlineText>{TRANSLATION_TEXT.dated_within_last_six_months}</InlineText>
            </ListItem>
          </NestedList>
        </WhiteBox>
        <EmailLink>
          <SecondText>{TRANSLATION_TEXT.please_email_documents}</SecondText>
          <StyledLink>compliance@rapaport.com</StyledLink>
        </EmailLink>
        <TextWrapper>
          <SecondText>
            {TRANSLATION_TEXT.a_rapnet_representative_will_follow_up_with_you}
          </SecondText>
          <SecondText>
            {TRANSLATION_TEXT.if_you_have_any_questions_please_feel_free}
            <StyledLink onClick={handleContactUsClick}>
              {TRANSLATION_TEXT.contact_us_lower}
            </StyledLink>
          </SecondText>
        </TextWrapper>
      </Card>
    </Container>
  );
};
